import { PDFDocument, StandardFonts, rgb, type PDFPage } from "pdf-lib";
import type {
  CSVReportData,
  CSVColumnSpec,
  AgencyBrandConfig,
} from "@agencylens/shared";
import {
  MARGIN_X,
  PAGE_H,
  PAGE_W,
  BODY,
  drawHeader,
  drawFooter,
  drawKpiGrid,
  drawTable,
  drawFallbackLineChart,
  fmtDateRange,
  fmtNumber,
  fmtPercent,
  type PDFFonts,
  type TableColumn,
} from "./render-utils.js";

export interface RenderCSVOptions {
  data: CSVReportData;
  agency: AgencyBrandConfig;
  /** Optional pre-rasterized chart PNG. If omitted, a pdf-lib fallback chart is drawn. */
  chartPng?: Uint8Array;
  /** Optional override title (defaults to filename) */
  title?: string;
}

function toNumber(v: string | number | undefined): number {
  if (typeof v === "number") return v;
  if (v == null) return 0;
  const n = Number(String(v).replace(/[^0-9.\-]/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function fmtCell(value: string | number | undefined, type: CSVColumnSpec["type"]): string {
  if (value == null) return "";
  switch (type) {
    case "number":
      return fmtNumber(toNumber(value));
    case "percent":
      return fmtPercent(typeof value === "number" ? value : Number(value));
    case "currency":
      return `$${fmtNumber(toNumber(value))}`;
    case "date":
    case "string":
    default:
      return String(value);
  }
}

/* --------------------------------------------------------- summary page */

function drawSummaryPage(
  page: PDFPage,
  fonts: PDFFonts,
  data: CSVReportData,
  brand: AgencyBrandConfig,
  title?: string,
): void {
  drawHeader(page, fonts, brand, "Custom Data Report");

  let y = PAGE_H - 120;
  page.drawText(title ?? data.upload.fileName, {
    x: MARGIN_X,
    y,
    size: 14,
    font: fonts.bold,
    color: rgb(0.2, 0.2, 0.2),
  });
  y -= 18;

  const startDate = String(data.rows[0]?.[data.dateColumn] ?? "");
  const endDate = String(data.rows[data.rows.length - 1]?.[data.dateColumn] ?? "");
  page.drawText(`${data.upload.rowCount} rows · ${fmtDateRange(startDate, endDate)}`, {
    x: MARGIN_X,
    y,
    size: 10,
    font: fonts.regular,
    color: rgb(0.45, 0.45, 0.45),
  });
  y -= 32;

  // KPI cards = totals/averages of first 4 metric columns
  const metrics = data.metricColumns.slice(0, 4);
  const cards = metrics.map((header) => {
    const col = data.upload.columns.find((c) => c.header === header);
    const type = col?.type ?? "number";
    const total = data.rows.reduce((s, r) => s + toNumber(r[header]), 0);
    if (type === "percent") {
      const avg = data.rows.length > 0 ? total / data.rows.length : 0;
      return { label: col?.label ?? header, value: fmtPercent(avg) };
    }
    return {
      label: col?.label ?? header,
      value:
        type === "currency"
          ? `$${fmtNumber(total)}`
          : fmtNumber(total),
    };
  });
  if (cards.length === 0) {
    cards.push({ label: "Rows", value: fmtNumber(data.upload.rowCount) });
  }
  drawKpiGrid(page, fonts, cards, y);
}

/* --------------------------------------------------------- chart page */

async function drawChartPage(
  doc: PDFDocument,
  page: PDFPage,
  fonts: PDFFonts,
  data: CSVReportData,
  brand: AgencyBrandConfig,
  chartPng?: Uint8Array,
): Promise<void> {
  drawHeader(page, fonts, brand, "Custom Data Report");

  const firstMetric = data.metricColumns[0];
  const title = firstMetric ? `${firstMetric} over time` : "Series";
  page.drawText(title, {
    x: MARGIN_X,
    y: PAGE_H - 120,
    size: 14,
    font: fonts.bold,
    color: rgb(0.2, 0.2, 0.2),
  });

  if (chartPng) {
    const png = await doc.embedPng(chartPng);
    const maxW = PAGE_W - 2 * MARGIN_X;
    const maxH = BODY - 160;
    const aspect = png.width / png.height;
    let w = maxW;
    let h = w / aspect;
    if (h > maxH) {
      h = maxH;
      w = h * aspect;
    }
    page.drawImage(png, {
      x: MARGIN_X + (maxW - w) / 2,
      y: PAGE_H - 140 - h,
      width: w,
      height: h,
    });
    return;
  }

  if (!firstMetric) {
    page.drawText("No numeric column available to chart.", {
      x: MARGIN_X,
      y: PAGE_H - 200,
      size: 11,
      font: fonts.regular,
      color: rgb(0.5, 0.5, 0.5),
    });
    return;
  }

  drawFallbackLineChart(
    page,
    fonts,
    brand,
    data.rows.map((r) => ({
      date: String(r[data.dateColumn] ?? ""),
      value: toNumber(r[firstMetric]),
    })),
    { topY: PAGE_H - 150 },
  );
}

/* --------------------------------------------------------- table pages */

function buildColumns(data: CSVReportData): TableColumn<Record<string, string | number>>[] {
  // Cap at 6 columns to fit on a US-letter page
  const cols = [data.dateColumn, ...data.metricColumns].slice(0, 6);
  // Distribute widths
  const total = PAGE_W - 2 * MARGIN_X;
  const dateW = 80;
  const otherW = (total - dateW) / Math.max(cols.length - 1, 1);
  return cols.map((header, i) => {
    const spec = data.upload.columns.find((c) => c.header === header);
    return {
      label: spec?.label ?? header,
      width: i === 0 ? dateW : otherW,
      align: i === 0 ? undefined : "right",
      format: (row) => fmtCell(row[header], spec?.type ?? "string"),
    };
  });
}

function drawTablePage(
  page: PDFPage,
  fonts: PDFFonts,
  cols: TableColumn<Record<string, string | number>>[],
  rows: Array<Record<string, string | number>>,
  brand: AgencyBrandConfig,
  pageLabel: string,
): void {
  drawHeader(page, fonts, brand, "Custom Data Report");
  page.drawText(pageLabel, {
    x: MARGIN_X,
    y: PAGE_H - 120,
    size: 14,
    font: fonts.bold,
    color: rgb(0.2, 0.2, 0.2),
  });
  drawTable(page, fonts, brand, cols, rows, PAGE_H - 150);
}

/* --------------------------------------------------------- main render */

const ROWS_PER_TABLE_PAGE = 31;

/**
 * Render a CSV PDF report.
 *
 * Layout:
 *   Page 1 — summary KPI cards (first 4 metric columns totals/averages)
 *   Page 2 — first-metric-over-date chart
 *   Page 3+ — data table (paginated, up to 6 columns)
 */
export async function renderCSVReport(opts: RenderCSVOptions): Promise<Uint8Array> {
  const { data, agency, chartPng, title } = opts;
  const doc = await PDFDocument.create();
  doc.setTitle(title ?? `${agency.agencyName} — ${data.upload.fileName}`);
  doc.setAuthor(agency.agencyName);
  doc.setProducer("AgencyLens");
  doc.setCreator("AgencyLens");
  doc.setCreationDate(new Date());

  const regular = await doc.embedFont(StandardFonts.Helvetica);
  const bold = await doc.embedFont(StandardFonts.HelveticaBold);
  const fonts: PDFFonts = { regular, bold };

  const summary = doc.addPage([PAGE_W, PAGE_H]);
  drawSummaryPage(summary, fonts, data, agency, title);

  const chartPage = doc.addPage([PAGE_W, PAGE_H]);
  await drawChartPage(doc, chartPage, fonts, data, agency, chartPng);

  const cols = buildColumns(data);
  const chunks: Array<Array<Record<string, string | number>>> = [];
  for (let i = 0; i < data.rows.length; i += ROWS_PER_TABLE_PAGE) {
    chunks.push(data.rows.slice(i, i + ROWS_PER_TABLE_PAGE));
  }
  if (chunks.length === 0) chunks.push([]);

  chunks.forEach((chunk, idx) => {
    const page = doc.addPage([PAGE_W, PAGE_H]);
    const label =
      chunks.length === 1
        ? "Data"
        : `Data (${idx + 1} of ${chunks.length})`;
    drawTablePage(page, fonts, cols, chunk, agency, label);
  });

  const pages = doc.getPages();
  const total = pages.length;
  pages.forEach((p, i) => drawFooter(p, fonts, agency, i + 1, total));

  return doc.save();
}
